// Force the LIGHT theme while printing. The dark theme prints as washed-out
// light-grey text (and dark note blocks) on white paper, so both the chat
// (Ctrl+P) and the article ("Print artikel") must always print in light.
//
// The theme lives on <html> as `data-theme` (the app's own dark rules) mirrored
// to the AFAS `theme` attribute (the --color-* tokens). Flipping both to light
// switches the whole document to light colours.
//
// - The chat prints via the browser's own Ctrl+P (no JS trigger), so a global
//   beforeprint/afterprint pair forces + restores the theme.
// - The article prints via a programmatic window.print(), which ALSO fires
//   beforeprint; it calls forcePrintLightTheme() synchronously first so the
//   forcing is guaranteed (not dependent on beforeprint timing).
//
// Everything goes through this one module so a programmatic print never
// double-saves the theme or restores it to the wrong (already-forced) value:
// `savedTheme` is the single source of truth and both operations are idempotent.

let savedTheme: { dataTheme: string | null; theme: string | null } | null = null;

/** Switch <html> to the light theme, remembering the previous theme once. */
export function forcePrintLightTheme(): void {
  if (savedTheme) return;
  const root = document.documentElement;
  savedTheme = {
    dataTheme: root.getAttribute("data-theme"),
    theme: root.getAttribute("theme"),
  };
  root.setAttribute("data-theme", "light");
  root.setAttribute("theme", "light");
}

/** Restore the theme saved by forcePrintLightTheme (no-op if not forced). */
export function restorePrintTheme(): void {
  if (!savedTheme) return;
  const root = document.documentElement;
  const { dataTheme, theme } = savedTheme;
  savedTheme = null;
  if (dataTheme === null) root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", dataTheme);
  if (theme === null) root.removeAttribute("theme");
  else root.setAttribute("theme", theme);
}

/** Install the global beforeprint/afterprint light-theme forcing (call once). */
export function installPrintLightTheme(): void {
  window.addEventListener("beforeprint", forcePrintLightTheme);
  window.addEventListener("afterprint", restorePrintTheme);
}
